import { logEvent } from '@amplitude/analytics-browser';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import CircularProgress from '@mui/material/CircularProgress';
import { DownloadIcon } from '../../components/Icons';
import Typography from '../../componentsv2/Typography';
import Button from '../../componentsv2/Button';
import { useTranslation } from '../../hooks/i18n';
import { IDocumentCategory } from '../../types/documents';
import { docsCollectionEvents } from '../../constants/events';

const ReadOnlyDocumentCategory = ({
  name,
  description,
  assets,
  isLoading,
  documentId,
  downloadDocument,
  groupName,
  keyName,
  template,
}: IDocumentCategory & {
  language: string;
  loadCompanyCategories: (companyId: number) => void;
  downloadDocument: (fileName: string, documentId: number) => void;
}) => {
  const { t } = useTranslation();

  return (
    <Grid container sx={{ paddingY: '2em' }} justifyContent="center" alignItems="center" gap="1em">
      <Grid item xs={12} md>
        <Typography sx={{ fontSize: '1.2em', fontWeight: 'bold' }}>{name}</Typography>
        <Typography>{description}</Typography>
        {!!template && (
          <Typography sx={{ marginTop: '8px', padding: '4px 0px' }}>
            <a href={template.url} download={template.fileName} target="_blank" rel="noreferrer">
              {t('client:documents.downloadTemplate')}
            </a>
          </Typography>
        )}
        {assets.length > 0 && (
          <Box sx={{ display: 'flex', alignItems: 'start', gap: '4px', marginTop: '1em' }}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={1.5}>
                <Typography sx={{ paddingTop: '0.5em', fontWeight: '600' }}>
                  {t('client:documents.uploaded')}:
                </Typography>
              </Grid>
              <Grid item xs={12} md>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    flexWrap: 'wrap',
                    maxWidth: '100%',
                    flex: 1,
                    gap: '8px',
                  }}
                >
                  {assets.map((asset) => (
                    <Box
                      key={asset.id}
                      sx={{
                        background: '#E6F6FB',
                        padding: '4px 10px',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        position: 'relative',
                      }}
                    >
                      {asset.isLoading && (
                        <Box sx={{ position: 'absolute', left: '45%', top: '25%' }}>
                          <CircularProgress size={14} />
                        </Box>
                      )}
                      <a
                        href={asset.url}
                        download={asset.fileName}
                        target="_blank"
                        rel="noreferrer"
                        onClick={() => {
                          logEvent(docsCollectionEvents.PREVIEW_DOC, { Name: asset.fileName });
                        }}
                      >
                        <Typography sx={{ visibility: asset.isLoading ? 'hidden' : 'initial' }}>
                          {asset.fileName}
                        </Typography>
                      </a>
                    </Box>
                  ))}
                </Box>
              </Grid>
            </Grid>
          </Box>
        )}
      </Grid>
      <Grid item>
        <Button
          type="link"
          color="secondary"
          startIcon={!isLoading ? <DownloadIcon /> : null}
          isLoading={isLoading}
          onClick={() => {
            downloadDocument(name, documentId);
            logEvent(docsCollectionEvents.CLICK_DOWNLOAD, {
              'Document Group': groupName,
              'Document Name': keyName,
            });
          }}
        >
          {t('client:documents.buttons.download')}
        </Button>
      </Grid>
    </Grid>
  );
};

export default ReadOnlyDocumentCategory;
